/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useQuery } from '@tanstack/react-query';
import {
  getBrokersByGroupId,
  getBrokersByGroupIdPagination,
} from './brokers.service';
import { BrokerPaginationParams } from './brokers';

// Query key constants
const QUERY_KEYS = {
  BROKER: 'broker',
  BROKERS: 'brokers',
  BY_DATABASE: 'by-database',
  BY_GROUP: 'by-group',
  BY_PRODUCT: 'by-product',
  BY_SUPPLIER: 'by-supplier',
  SUPPLIERS_BY_PRODUCT: 'suppliers-by-product',
} as const;

const brokerKeys = {
  all: () => [QUERY_KEYS.BROKER] as const,
  brokers: () => [...brokerKeys.all(), QUERY_KEYS.BROKERS] as const,
  brokersByGroup: (groupId: string) =>
    [...brokerKeys.brokers(), QUERY_KEYS.BY_GROUP, groupId] as const,

  brokersByGroupPagination: (
    groupId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      type?: string;
    },
  ) =>
    [
      ...brokerKeys.brokers(),
      QUERY_KEYS.BY_GROUP,
      groupId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'created_at',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        type: options?.type || '',
      },
    ] as const,
};

// Hook to get brokers by group ID with optional search filtering
export const useGetBrokersByGroupId = (groupId: string) => {
  const { data, error, refetch } = useQuery({
    queryKey: brokerKeys.brokersByGroup(groupId),
    queryFn: () => getBrokersByGroupId(groupId),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(groupId),
  });

  return {
    data: data || [],
    error: error,
    refetch: refetch,
  };
};

export const useGetBrokersByGroupIdPagination = (
  groupId: string,
  searchTerm = '',
  searchOptions: BrokerPaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: brokerKeys.brokersByGroupPagination(groupId, {
      page: searchOptions.page,
      limit: searchOptions.limit,
      sortBy: searchOptions.sortBy,
      sortOrder: searchOptions.sortOrder,
      search: searchTerm,
      type: searchOptions.type,
    }),
    queryFn: () =>
      getBrokersByGroupIdPagination(groupId, {
        ...searchOptions,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(groupId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};
